import { useState } from 'react';
import { IconDoor, IconHome, IconLink, IconMute, IconSound } from '../Icons';
import { Sheet } from '../Sheet';
import { Tibbs } from '../Art';

export function TableMenu({
  open,
  onClose,
  mode,
  isHost,
  muted,
  tapTwice,
  hints,
  roomCode,
  onMute,
  onTapTwice,
  onHints,
  onHelp,
  onHome,
  onQuit,
  onLobby,
  onCopyLink,
  tibbs,
}: {
  /** Tibbs's voice: volume (0 to 100) and captions. He only speaks when asked. */
  tibbs?: { vol: number; captions: boolean; onVol: (v: number) => void; onCaptions: () => void };
  open: boolean;
  onClose: () => void;
  mode: 'solo' | 'online';
  isHost: boolean;
  muted: boolean;
  tapTwice: boolean;
  /** Vs computer: the "Hint button" switch. */
  hints?: boolean;
  roomCode?: string;
  onMute: () => void;
  onTapTwice: () => void;
  onHints?: () => void;
  onHelp: () => void;
  onHome: () => void;
  onQuit?: () => void;
  onLobby?: () => void;
  onCopyLink?: () => void;
}) {
  const [confirm, setConfirm] = useState<null | 'quit' | 'lobby'>(null);
  const close = () => {
    setConfirm(null);
    onClose();
  };
  return (
    <Sheet open={open} onClose={close} title={confirm ? 'Are you sure?' : 'Menu'} labelledBy="menu-title">
      {confirm ? (
        <div className="confirm">
          <p>
            {confirm === 'quit'
              ? mode === 'solo'
                ? 'Quit this game? It will be gone for good.'
                : 'Leave this game? The computer will play your seat until you come back.'
              : 'End the game for everyone and go back to the lobby?'}
          </p>
          <div className="sheet-actions">
            <button className="btn btn-ink" onClick={() => setConfirm(null)} data-autofocus>
              Keep playing
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                const which = confirm;
                close();
                if (which === 'quit') onQuit?.();
                else onLobby?.();
              }}
            >
              {confirm === 'quit' ? (mode === 'solo' ? 'Quit game' : 'Leave game') : 'End game'}
            </button>
          </div>
        </div>
      ) : (
        <ul className="menu-list">
          <li>
            <button className="menu-item" onClick={() => { close(); onHelp(); }}>
              <span className="menu-ic help-glyph" aria-hidden="true">?</span> How to play
            </button>
          </li>
          <li>
            <button className="menu-item" onClick={onMute} aria-pressed={!muted}>
              <span className="menu-ic">{muted ? <IconMute /> : <IconSound />}</span> Sound {muted ? 'off' : 'on'}
            </button>
          </li>
          <li>
            <button className="menu-item" onClick={onTapTwice} aria-pressed={tapTwice}>
              <span className="menu-ic" aria-hidden="true">
                <span className={`switch${tapTwice ? ' on' : ''}`} />
              </span>{' '}
              Tap twice to swap
            </button>
          </li>
          {tibbs && (
            <li className="menu-tibbs">
              <label className="menu-item menu-slider">
                <span className="menu-ic" aria-hidden="true">
                  <Tibbs pose="wry" size={24} />
                </span>
                <span className="menu-slider-label">Tibbs’s voice</span>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={5}
                  value={tibbs.vol}
                  aria-label="Tibbs’s voice volume"
                  onChange={(e) => tibbs.onVol(Number(e.target.value))}
                />
              </label>
              <button className="menu-item" onClick={tibbs.onCaptions} aria-pressed={tibbs.captions}>
                <span className="menu-ic" aria-hidden="true">
                  <span className={`switch${tibbs.captions ? ' on' : ''}`} />
                </span>{' '}
                Tibbs’s captions
              </button>
            </li>
          )}
          {mode === 'solo' && onHints && (
            <li>
              <button className="menu-item" onClick={onHints} aria-pressed={!!hints}>
                <span className="menu-ic" aria-hidden="true">
                  <span className={`switch${hints ? ' on' : ''}`} />
                </span>{' '}
                Hint button
              </button>
            </li>
          )}
          {mode === 'online' && roomCode && onCopyLink && (
            <li>
              <button className="menu-item" onClick={onCopyLink}>
                <span className="menu-ic"><IconLink /></span> Copy invite link
              </button>
            </li>
          )}
          {mode === 'online' && isHost && onLobby && (
            <li>
              <button className="menu-item" onClick={() => setConfirm('lobby')}>
                <span className="menu-ic"><IconDoor /></span> End game, back to lobby
              </button>
            </li>
          )}
          <li>
            <button className="menu-item" onClick={() => { close(); onHome(); }}>
              <span className="menu-ic"><IconHome /></span> {mode === 'solo' ? 'Back to home (your game is saved)' : 'Back to home (your seat is kept)'}
            </button>
          </li>
          {onQuit && (
            <li>
              <button className="menu-item danger" onClick={() => setConfirm('quit')}>
                <span className="menu-ic"><IconDoor /></span> {mode === 'solo' ? 'Quit this game' : 'Leave game'}
              </button>
            </li>
          )}
        </ul>
      )}
    </Sheet>
  );
}
